import { FieldOperationCondition, FieldOperationConditionOperator, FieldOperationOperatorCondition, FieldType, UUID } from '@indigohive/cogfy-types'
import { useCogfy } from '../../hooks'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button'
import { PlusIcon } from 'lucide-react'
import { OperationCondition } from './subcomponents'
import { Select } from '../Select'

const OPERATION_CONDITIONS_OPERATOR_BY_PROPERTY_TYPE: Record<'text' | 'number' | 'date' | 'boolean', { label: string, value: FieldOperationConditionOperator }[]> = {
  text: [
    { label: 'contains', value: 'contains' },
    { label: 'ends with', value: 'ends_with' },
    { label: 'equals', value: 'equals' },
    { label: 'has error', value: 'has_error' },
    { label: 'has no error', value: 'has_no_error' },
    { label: 'not contains', value: 'not_contains' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'starts with', value: 'starts_with' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  number: [
    { label: 'equals', value: 'equals' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'greater than or equals', value: 'greater_than_or_equals' },
    { label: 'has error', value: 'has_error' },
    { label: 'has no error', value: 'has_no_error' },
    { label: 'less than', value: 'less_than' },
    { label: 'less than or equals', value: 'less_than_or_equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  date: [
    { label: 'equals', value: 'equals' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'greater than or equals', value: 'greater_than_or_equals' },
    { label: 'has error', value: 'has_error' },
    { label: 'has no error', value: 'has_no_error' },
    { label: 'less than', value: 'less_than' },
    { label: 'less than or equals', value: 'less_than_or_equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' }
  ],
  boolean: [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'has error', value: 'has_error' },
    { label: 'has no error', value: 'has_no_error' }
  ]
}

export type OperationConditionFormProps = {
  onChange: (value: FieldOperationCondition) => void
  operationCondition?: FieldOperationCondition
  fields?: { id: UUID, name: string, type: FieldType }[]
}

export function OperationConditionForm (props: OperationConditionFormProps) {
  const { onChange, operationCondition, fields = [] } = props

  const { t } = useTranslation()
  const cogfy = useCogfy()
  const getFieldProperties = useQuery({
    queryKey: ['getFieldProperties'],
    queryFn: ({ signal }) => cogfy.getFieldProperties({ signal })
  })
  const fieldProperties = getFieldProperties.data?.data ?? {}
  const operationConditionData = (operationCondition?.data ?? []) as FieldOperationOperatorCondition[]

  const inputOptions = fields.flatMap(field => {
    return fieldProperties?.[field.type]?.map(property => {
      if (property.name === 'value') {
        return { value: field.id + ':' + property.name, label: field.name }
      }
      return { value: field.id + ':' + property.name, label: field.name + ': ' + property.name }
    }) ?? []
  })

  const operatorOptions = useCallback((fieldId?: UUID, inputProperty?: string) => {
    const fieldType = fields.find(field => field.id === fieldId)?.type

    if (!fieldId || !fieldType) return []

    const propertyType = fieldProperties[fieldType]?.find(property => property.name === inputProperty)?.type

    if (!propertyType) return []

    const operators = OPERATION_CONDITIONS_OPERATOR_BY_PROPERTY_TYPE[propertyType]

    return operators.map(operator => ({ value: operator.value, label: t(`conditions:${operator.label}`) }))
  }, [fieldProperties, fields])

  return (
    <div className="my-2">
      <div className="flex items-center">
        <div className="grow text-sm">
          {t('editFieldDrawer:Conditions')}
        </div>
        <Button
          ghost
          square
          size="sm"
          onClick={() => {
            onChange({
              operator: operationCondition?.operator ?? 'and',
              data: [
                ...(operationCondition?.data ?? []),
                {}
              ]
            })
          }}
        >
          <PlusIcon size={16} />
        </Button>
      </div>
      {operationConditionData.length === 0 && (
        <div className="text-sm opacity-40">
          {t('editFieldDrawer:No conditions set')}
        </div>
      )}
      <div>
        {operationConditionData.map((condition, index) => (
          <div
            className="flex flex-row gap-2 py-1 items-center"
            key={`${index}-${condition.input?.fieldId}`}
          >
            {operationConditionData.length > 1 && (
              <div className="text-sm min-w-20 text-center">
                {index === 0 && (
                  <div className="opacity-40">
                    {t('Where')}
                  </div>
                )}
                {index === 1 && (
                  <Select
                    size="md"
                    options={[
                      { label: t('conditions:AND'), value: 'and' },
                      { label: t('conditions:OR'), value: 'or' }
                    ]}
                    value={operationCondition?.operator ?? 'and'}
                    onChange={event => {
                      onChange({
                        operator: event.target.value as 'and' | 'or',
                        data: operationConditionData
                      })
                    }}
                  />
                )}
                {index > 1 && (
                  <div className="opacity-40">
                    {t(`conditions:${operationCondition?.operator?.toUpperCase()}`)}
                  </div>
                )}
              </div>
            )}

            <OperationCondition
              condition={condition}
              fields={fields}
              inputOptions={inputOptions}
              fieldProperties={{ data: fieldProperties }}
              operatorOptions={operatorOptions}
              onChange={newCondition => {
                onChange({
                  operator: operationCondition?.operator ?? 'and',
                  data: operationConditionData.map((c, i) => i === index ? newCondition : c)
                })
              }}
              onDelete={() => {
                onChange({
                  operator: operationCondition?.operator ?? 'and',
                  data: operationConditionData.filter((_, i) => i !== index)
                })
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
